import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

// Floating EN/ES language toggle for the marketing page. 1:1 in spirit
// with la-capital-2026/js/translate-widget.js — circular button at
// bottom-right showing the TARGET language's flag, dismissable via the ×
// corner button with a Yes/No confirmation, and the dismissal is cached
// in localStorage so a returning visitor doesn't see it again.
//
// The widget never short-circuits the underlying `react-i18next` instance —
// it just calls `i18n.changeLanguage(...)` then hard-reloads, mirroring the
// same React 19 + StrictMode workaround documented in
// `components/layout/LanguageSwitcher.tsx`.

const DISMISS_KEY = 'voysi.lang-widget-dismissed'

const FLAGS: Record<'en' | 'es', string> = {
  en: '/brand/flags/us.webp',
  es: '/brand/flags/es.webp',
}

const LABELS: Record<'en' | 'es', string> = {
  en: 'EN',
  es: 'ES',
}

type Lang = 'en' | 'es'

function normalizeLang(raw: string | undefined): Lang {
  return raw?.toLowerCase().startsWith('es') ? 'es' : 'en'
}

export function LanguageToggleWidget() {
  const { t, i18n } = useTranslation('marketing')
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(DISMISS_KEY) === '1'
  })
  const [confirming, setConfirming] = useState(false)

  // Re-render when the page's language changes (e.g. after a hot reload of
  // i18n.changeLanguage that didn't trigger a window reload). Without this,
  // the flag/aria-label can briefly desync from `i18n.language`.
  const [, force] = useState(0)
  useEffect(() => {
    const onChange = () => force((n) => n + 1)
    i18n.on('languageChanged', onChange)
    return () => {
      i18n.off('languageChanged', onChange)
    }
  }, [i18n])

  if (dismissed) return null

  const current: Lang = normalizeLang(i18n.language)
  const target: Lang = current === 'en' ? 'es' : 'en'

  const handleToggle = () => {
    void i18n.changeLanguage(target).finally(() => {
      // Same React 19 + StrictMode reload workaround as
      // LanguageSwitcher.tsx — the captured `t()` refs don't always refresh
      // after `changeLanguage`. A hard reload is the cheapest fix.
      window.location.reload()
    })
  }

  const handleConfirmYes = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, '1')
    } catch {
      // Ignore quota/private-mode failures; the widget still hides for
      // this session.
    }
    setDismissed(true)
  }

  return (
    <div className="fixed bottom-3 right-3 z-50 flex flex-col items-end gap-3">
      {confirming && (
        <div
          role="dialog"
          aria-label={t('widget.confirmHide')}
          className="rounded-xl border border-ink-200 bg-white px-3 py-2 shadow-lg"
        >
          <div className="mb-1.5 text-center text-[13px] font-semibold text-ink-900 whitespace-nowrap">
            {t('widget.confirmHide')}
          </div>
          <div className="flex justify-center gap-2">
            <button
              type="button"
              onClick={handleConfirmYes}
              className="rounded-md bg-flamingo-500 px-3 py-1 text-[12px] font-semibold text-white hover:bg-flamingo-600"
            >
              {t('widget.yes')}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="rounded-md border border-ink-200 bg-white px-3 py-1 text-[12px] font-semibold text-ink-700 hover:bg-ink-50"
            >
              {t('widget.no')}
            </button>
          </div>
        </div>
      )}

      <div className="relative">
        {/* Pulsing halo: uses Tailwind's built-in `animate-ping` (scale 1→2
            with fade) since the existing voysiPulse keyframe scales inward
            and would be hidden behind the opaque button. animate-ping is
            global to Tailwind so no new CSS is added. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-full bg-flamingo-500 opacity-60 animate-ping"
        />

        <button
          type="button"
          onClick={handleToggle}
          aria-label={target === 'en' ? t('widget.switchToEn') : t('widget.switchToEs')}
          className="relative flex h-[60px] w-[60px] items-center justify-center rounded-full bg-flamingo-500 shadow-lg transition-transform hover:scale-[1.08] focus:outline-none focus:ring-2 focus:ring-flamingo-500 focus:ring-offset-2"
        >
          <img
            src={FLAGS[target]}
            alt=""
            aria-hidden="true"
            className="h-10 w-10 rounded-full object-cover"
          />
          {/* Dark overlay so the EN/ES text reads regardless of flag colors. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute h-10 w-10 rounded-full"
            style={{ background: 'rgba(0,0,0,0.25)' }}
          />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute text-[15px] font-bold tracking-wide text-white"
            style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}
          >
            {LABELS[target]}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setConfirming((c) => !c)}
          aria-label={t('widget.dismiss')}
          className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-ink-200 bg-white text-[14px] font-bold leading-none text-ink-500 shadow-sm hover:bg-ink-50 hover:text-ink-900 focus:outline-none focus:ring-2 focus:ring-flamingo-500"
        >
          ×
        </button>
      </div>
    </div>
  )
}
